import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert,
    CircularProgress,
    Typography,
    Divider,
    Box,
    Switch,
    FormControlLabel
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createClient, updateClient } from '../../store/slices/clientSlice';
import { clientApi, Client, CreateClientData, UpdateClientData } from '../../services/clientApi';
import { useTheme } from '../../contexts/ThemeContext';

interface ClientFormProps {
    open: boolean;
    client: Client | null;
    onClose: () => void;
    onSuccess: (isEdit: boolean, clientName: string) => void;
    onError?: (message: string) => void;
}

const ClientForm: React.FC<ClientFormProps> = ({ open, client, onClose, onSuccess, onError }) => {
    const { theme } = useTheme();
    const dispatch = useAppDispatch();
    const { loading, error } = useAppSelector((state) => state.clients);

    // Form state
    const [formData, setFormData] = useState<CreateClientData & { gst_enabled?: boolean }>({
        name: '',
        email: '',
        password: '',
        address: '',
        area_zone: undefined,
        point_of_sale: undefined,
        account_type: '',
        business_registration_status: '',
        company_name: '',
        authorized_person_name: '',
        authorized_designation: '',
        authorized_phone: '',
        authorized_cnic: '',
        admin_contact_name: '',
        admin_phone: '',
        finance_contact_name: '',
        web_address: '',
        brand_name: '',
        ntn: '',
        gst: '',
        gst_enabled: true,
        business_sector: '',
        products: '',
        pickup_address: '',
        pickup_contact_person: '',
        pickup_phone: '',
        pickup_email: '',
        branch_id: undefined
    });

    // Dropdown data
    const [branches, setBranches] = useState<Array<{ id: number; name: string; code: string }>>([]);
    const [cities, setCities] = useState<Array<{ id: number; name: string; code: string }>>([]);
    const [loadingDropdowns, setLoadingDropdowns] = useState(false);
    const [loadingClient, setLoadingClient] = useState(false);

    // Validation errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Helper to populate form from client data
    const populateFormFromClient = (clientData: Client) => {
        // API may return client_profile (snake_case) or clientProfile (camelCase)
        const profile = clientData.clientProfile || clientData.client_profile;

        // Helper to get area_zone ID (could be number or object with id)
        const getAreaZoneId = () => {
            if (!profile?.area_zone) return undefined;
            if (typeof profile.area_zone === 'object') return (profile.area_zone as any).id;
            return Number(profile.area_zone);
        };

        // Helper to get point_of_sale ID (could be number or object with id)
        const getPointOfSaleId = () => {
            if (!profile?.point_of_sale) return undefined;
            if (typeof profile.point_of_sale === 'object') return (profile.point_of_sale as any).id;
            return Number(profile.point_of_sale);
        };

        setFormData({
            name: clientData.name || '',
            email: clientData.email || '',
            password: '',
            address: profile?.address || '',
            area_zone: getAreaZoneId(),
            point_of_sale: getPointOfSaleId(),
            account_type: profile?.account_type || '',
            business_registration_status: profile?.business_registration_status || '',
            company_name: profile?.company_name || '',
            authorized_person_name: profile?.authorized_person_name || '',
            authorized_designation: profile?.authorized_designation || '',
            authorized_phone: profile?.authorized_phone || '',
            authorized_cnic: profile?.authorized_cnic || '',
            admin_contact_name: profile?.admin_contact_name || '',
            admin_phone: profile?.admin_phone || '',
            finance_contact_name: profile?.finance_contact_name || '',
            web_address: profile?.web_address || '',
            brand_name: profile?.brand_name || '',
            ntn: profile?.ntn || '',
            gst: profile?.gst || '',
            gst_enabled: profile?.gst_enabled !== undefined ? profile.gst_enabled : true,
            business_sector: profile?.business_sector || '',
            products: profile?.products || '',
            pickup_address: profile?.pickup_address || '',
            pickup_contact_person: profile?.pickup_contact_person || '',
            pickup_phone: profile?.pickup_phone || '',
            pickup_email: profile?.pickup_email || '',
            branch_id: clientData.branch_id || undefined
        });
    };

    // Load dropdown data
    useEffect(() => {
        if (open) {
            loadDropdownData();
        }
    }, [open]);

    // Load client data for editing - fetch full details from API
    useEffect(() => {
        if (client && open) {
            // Fetch full client details from API to ensure all fields are populated
            const fetchClientDetails = async () => {
                setLoadingClient(true);
                try {
                    const fullClient = await clientApi.getClient(client.id);
                    populateFormFromClient(fullClient);
                } catch (error) {
                    console.error('Error fetching client details:', error);
                    // Fall back to using the passed client data
                    populateFormFromClient(client);
                } finally {
                    setLoadingClient(false);
                }
            };
            fetchClientDetails();
        } else if (!client && open) {
            // Reset form for new client
            setFormData({
                name: '',
                email: '',
                password: '',
                address: '',
                area_zone: undefined,
                point_of_sale: undefined,
                account_type: '',
                business_registration_status: '',
                company_name: '',
                authorized_person_name: '',
                authorized_designation: '',
                authorized_phone: '',
                authorized_cnic: '',
                admin_contact_name: '',
                admin_phone: '',
                finance_contact_name: '',
                web_address: '',
                brand_name: '',
                ntn: '',
                gst: '',
                gst_enabled: true,
                business_sector: '',
                products: '',
                pickup_address: '',
                pickup_contact_person: '',
                pickup_phone: '',
                pickup_email: '',
                branch_id: undefined
            });
        }
    }, [client, open]);

    const loadDropdownData = async () => {
        setLoadingDropdowns(true);
        try {
            const [branchesData, citiesData] = await Promise.all([
                clientApi.getBranches(),
                clientApi.getCities()
            ]);
            setBranches(branchesData);
            setCities(citiesData);
        } catch (error) {
            console.error('Error loading dropdown data:', error);
        } finally {
            setLoadingDropdowns(false);
        }
    };

    const handleInputChange = (field: string, value: string | number | boolean | undefined) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error for this field
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!client && !formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password && formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!formData.authorized_phone?.trim()) {
            newErrors.authorized_phone = 'Authorized person phone is required';
        }

        if (!formData.branch_id) {
            newErrors.branch_id = 'Branch is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            if (client) {
                // Update existing client
                const updateData: UpdateClientData = { ...formData };
                delete (updateData as any).password; // Remove password from update data

                await dispatch(updateClient({ id: client.id, clientData: updateData })).unwrap();
                onSuccess(true, formData.name);
            } else {
                // Create new client
                await dispatch(createClient(formData)).unwrap();
                onSuccess(false, formData.name);
            }
        } catch (error: any) {
            console.error('Error saving client:', error);
            if (onError) {
                onError(error?.message || 'An error occurred while saving the client');
            }
        }
    };

    const handleClose = () => {
        setErrors({});
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 3,
                }
            }}
        >
            <DialogTitle sx={{ 
                pb: 1, 
                mb:2,
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: `1px solid ${theme.palette.divider}`
            }}>
                <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                    {client ? 'Edit Client' : 'Add New Client'}
                </Typography>
                <Button
                    onClick={handleClose}
                    sx={{ 
                        color: theme.palette.text.secondary,
                        minWidth: 'auto',
                        p: 1
                    }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {loadingClient ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                <Grid container spacing={3}>
                    {/* Basic Information */}
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 600 }}>
                            Basic Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Full Name *"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            error={!!errors.name}
                            helperText={errors.name}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Email *"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            error={!!errors.email}
                            helperText={errors.email}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label={client ? 'New Password (leave blank to keep current)' : 'Password *'}
                            type="password"
                            value={formData.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            error={!!errors.password}
                            helperText={errors.password}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth error={!!errors.branch_id}>
                            <InputLabel>Branch *</InputLabel>
                            <Select
                                value={formData.branch_id || ''}
                                onChange={(e) => handleInputChange('branch_id', e.target.value ? Number(e.target.value) : undefined)}
                                label="Branch *"
                                disabled={loadingDropdowns}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select Branch</em>
                                </MenuItem>
                                {branches.map((branch) => (
                                    <MenuItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.branch_id && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {errors.branch_id}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 600 }}>
                            Contact Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Authorized Person Phone *"
                            value={formData.authorized_phone}
                            onChange={(e) => handleInputChange('authorized_phone', e.target.value)}
                            error={!!errors.authorized_phone}
                            helperText={errors.authorized_phone}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Admin Contact Phone"
                            value={formData.admin_phone}
                            onChange={(e) => handleInputChange('admin_phone', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Authorized Person CNIC"
                            value={formData.authorized_cnic}
                            onChange={(e) => handleInputChange('authorized_cnic', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Area Zone</InputLabel>
                            <Select
                                value={formData.area_zone || ''}
                                onChange={(e) => handleInputChange('area_zone', e.target.value ? Number(e.target.value) : undefined)}
                                label="Area Zone"
                                disabled={loadingDropdowns}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select Area Zone</em>
                                </MenuItem>
                                {cities.map((city) => (
                                    <MenuItem key={city.id} value={city.id}>
                                        {city.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Point of Sale</InputLabel>
                            <Select
                                value={formData.point_of_sale || ''}
                                onChange={(e) => handleInputChange('point_of_sale', e.target.value ? Number(e.target.value) : undefined)}
                                label="Point of Sale"
                                disabled={loadingDropdowns}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            >
                                <MenuItem value="">
                                    <em>Select Point of Sale</em>
                                </MenuItem>
                                {cities.map((city) => (
                                    <MenuItem key={city.id} value={city.id}>
                                        {city.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Authorized Person Name"
                            value={formData.authorized_person_name}
                            onChange={(e) => handleInputChange('authorized_person_name', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Authorized Person Designation"
                            value={formData.authorized_designation}
                            onChange={(e) => handleInputChange('authorized_designation', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Admin Contact Name"
                            value={formData.admin_contact_name}
                            onChange={(e) => handleInputChange('admin_contact_name', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Finance Contact Name"
                            value={formData.finance_contact_name}
                            onChange={(e) => handleInputChange('finance_contact_name', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Address"
                            multiline
                            rows={3}
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 600 }}>
                            Company Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            value={formData.company_name}
                            onChange={(e) => handleInputChange('company_name', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="NTN (National Tax Number)"
                            value={formData.ntn}
                            onChange={(e) => handleInputChange('ntn', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="GST Number"
                            value={formData.gst}
                            onChange={(e) => handleInputChange('gst', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.gst_enabled ?? true}
                                    onChange={(e) => handleInputChange('gst_enabled', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Apply GST on Consignments"
                            sx={{
                                mt: 1,
                                '& .MuiFormControlLabel-label': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6 }}>
                            When enabled, GST will be applied to this client's consignments
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Account Type"
                            value={formData.account_type}
                            onChange={(e) => handleInputChange('account_type', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Business Registration Status"
                            value={formData.business_registration_status}
                            onChange={(e) => handleInputChange('business_registration_status', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Brand Name"
                            value={formData.brand_name}
                            onChange={(e) => handleInputChange('brand_name', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Web Address"
                            value={formData.web_address}
                            onChange={(e) => handleInputChange('web_address', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Business Sector"
                            value={formData.business_sector}
                            onChange={(e) => handleInputChange('business_sector', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Products"
                            value={formData.products}
                            onChange={(e) => handleInputChange('products', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 600 }}>
                            Pickup Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Pickup Address"
                            multiline
                            rows={3}
                            value={formData.pickup_address}
                            onChange={(e) => handleInputChange('pickup_address', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Pickup Contact Person"
                            value={formData.pickup_contact_person}
                            onChange={(e) => handleInputChange('pickup_contact_person', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Pickup Phone"
                            value={formData.pickup_phone}
                            onChange={(e) => handleInputChange('pickup_phone', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Pickup Email"
                            type="email"
                            value={formData.pickup_email}
                            onChange={(e) => handleInputChange('pickup_email', e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Grid>
                </Grid>
                )}
            </DialogContent>

            <DialogActions sx={{ 
                p: 3, 
                pt: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                gap: 2
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor: theme.palette.divider,
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            borderColor: theme.palette.text.secondary,
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || loadingClient}
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        borderRadius: 2,
                        '&:hover': {
                            bgcolor: theme.palette.secondary.dark,
                        },
                        '&:disabled': {
                            bgcolor: theme.palette.action.disabled,
                        }
                    }}
                >
                    {loading ? 'Saving...' : (client ? 'Update Client' : 'Create Client')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClientForm;
